/**
 * Voters — the server-side import, as a resumable chunk loop.
 *
 * One batch is created (`POST VoterImports` → "Import #n"), then the rows go up as raw spreadsheet
 * cells in fixed-size chunks (`PUT VoterImports/{id}/chunks/{k}`). The server applies the column
 * mapping, validates, de-duplicates and records every rejected row against the batch, so "rows to
 * fix" survives a refresh and shows up in Import history.
 *
 * The rules this loop keeps, each one a test in `__tests__/batch-uploader.test.ts`:
 *
 *  - Chunk boundaries are a pure function of the row list and the chunk size, so a resumed import
 *    resends exactly the chunk the server is waiting for (`nextChunkIndex`), and a replayed chunk is
 *    idempotent server-side.
 *  - Transient failures (network, 5xx, 503 CORE_UNAVAILABLE — nothing was recorded) retry 3 times
 *    with backoff. 429 waits the server's Retry-After. 401 PAUSES — the session ended; a retry loop
 *    would just spin — and resumes when the caller says so. 409 (chunk replay with different
 *    content) is fatal: the file changed under the batch.
 *  - Abort stops before the next chunk and NEVER calls complete. An aborted batch stays Open and
 *    reads as "Incomplete" in history, which is the truth.
 *
 * No React and no axios here — the transport is injected, so the whole state machine runs in a test
 * with a fake server.
 */

import type { IChunkResult, IChunkRow, ICreatedBatch, ICreateImportRequest } from '../api/voters-api';

export interface IBatchTransport {
	createBatch: (request: ICreateImportRequest) => Promise<ICreatedBatch>;
	putChunk: (id: string, chunkIndex: number, body: { firstRowNumber: number; cells: string[][] }) => Promise<IChunkResult>;
	getBatch: (id: string) => Promise<{ nextChunkIndex: number }>;
	complete: (id: string) => Promise<void>;
}

export interface IUploadControl {
	aborted: boolean;
	paused: boolean;
}

export interface IBatchProgress {
	batchId: string;
	number: number;
	chunkIndex: number;
	chunkCount: number;
	rowsSent: number;
	rowCount: number;
	added: number;
	failed: number;
	/** Set while the loop is waiting out a 429 or a backoff, for the status line. */
	waiting?: 'rate-limited' | 'retrying' | 'signed-out';
}

export interface IBatchUploadOptions {
	transport: IBatchTransport;
	/**
	 * Rows to send, in order. Their row numbers MUST run consecutively from the first: the server
	 * numbers a chunk's rows as `firstRowNumber + i`, which is how its failures name them.
	 */
	rows: readonly IChunkRow[];
	chunkSize: number;
	/** Present to resume an existing batch; absent to create one. */
	resume?: { id: string; number: number };
	create: Omit<ICreateImportRequest, 'expectedRowCount' | 'expectedChunkCount'>;
	control: IUploadControl;
	onBatchCreated?: (batch: { id: string; number: number; chunkCount: number; sameFileNumbers: number[] }) => void;
	onChunk?: (chunkIndex: number, rows: readonly IChunkRow[], result: IChunkResult) => void;
	onProgress?: (progress: IBatchProgress) => void;
	/** Injected so tests do not wait real seconds. */
	sleep?: (ms: number) => Promise<void>;
	maxRetries?: number;
}

export type BatchUploadOutcome = 'completed' | 'aborted' | 'failed';

export interface IBatchUploadResult {
	outcome: BatchUploadOutcome;
	batchId: string;
	number: number;
	added: number;
	failed: number;
	/** Index of the first chunk NOT confirmed by the server — where a resume starts. */
	nextChunkIndex: number;
	error?: unknown;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const chunkCountFor = (rowCount: number, chunkSize: number): number => Math.ceil(rowCount / Math.max(1, chunkSize));

export const sliceChunk = <T>(rows: readonly T[], chunkIndex: number, chunkSize: number): T[] =>
	rows.slice(chunkIndex * chunkSize, (chunkIndex + 1) * chunkSize);

const statusOf = (error: unknown): number | undefined => (error as { response?: { status?: number } })?.response?.status;

const retryAfterMs = (error: unknown): number => {
	const headers = (error as { response?: { headers?: Record<string, unknown> } })?.response?.headers;
	const seconds = Number(headers?.['retry-after'] ?? headers?.['Retry-After']);
	return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 120) * 1000 : 5000;
};

/** Network error, any 5xx (503 included — the server recorded nothing), or a timeout. */
export const isTransient = (error: unknown): boolean => {
	const status = statusOf(error);
	return status === undefined || status >= 500 || status === 408;
};

const waitWhilePaused = async (control: IUploadControl, sleep: (ms: number) => Promise<void>) => {
	while (control.paused && !control.aborted) await sleep(200);
};

export const runBatchUpload = async (options: IBatchUploadOptions): Promise<IBatchUploadResult> => {
	const { transport, rows, control, onProgress, onChunk } = options;
	const sleep = options.sleep ?? defaultSleep;
	const maxRetries = options.maxRetries ?? 3;
	const chunkSize = Math.max(1, options.chunkSize);
	const chunkCount = chunkCountFor(rows.length, chunkSize);

	const totals = { added: 0, failed: 0 };

	let batch: { id: string; number: number } = { id: '', number: 0 };
	let sameFileNumbers: number[] = [];
	let startChunk = 0;

	const result = (outcome: BatchUploadOutcome, nextChunkIndex: number, error?: unknown): IBatchUploadResult => ({
		outcome,
		batchId: batch.id,
		number: batch.number,
		...totals,
		nextChunkIndex,
		error,
	});

	try {
		if (options.resume) {
			batch = options.resume;
			startChunk = Math.max(0, Math.min(chunkCount, (await transport.getBatch(batch.id)).nextChunkIndex));
		} else {
			const created = await transport.createBatch({
				...options.create,
				expectedRowCount: rows.length,
				expectedChunkCount: chunkCount,
			});
			batch = { id: created.id, number: created.number };
			sameFileNumbers = created.sameFileNumbers;
		}
	} catch (error) {
		return result('failed', 0, error);
	}

	options.onBatchCreated?.({ ...batch, chunkCount, sameFileNumbers });

	const progress = (chunkIndex: number, waiting?: IBatchProgress['waiting']) =>
		onProgress?.({
			batchId: batch.id,
			number: batch.number,
			chunkIndex,
			chunkCount,
			rowsSent: Math.min(rows.length, chunkIndex * chunkSize),
			rowCount: rows.length,
			...totals,
			waiting,
		});

	progress(startChunk);

	for (let chunkIndex = startChunk; chunkIndex < chunkCount; chunkIndex++) {
		await waitWhilePaused(control, sleep);
		if (control.aborted) return result('aborted', chunkIndex);

		const chunkRows = sliceChunk(rows, chunkIndex, chunkSize);
		let attempt = 0;
		let rateLimitWaits = 0;

		for (;;) {
			try {
				const chunk = await transport.putChunk(batch.id, chunkIndex, {
					firstRowNumber: chunkRows[0]?.rowNumber ?? 1,
					cells: chunkRows.map((row) => row.cells),
				});
				totals.added += chunk.added;
				totals.failed += chunk.failed;
				onChunk?.(chunkIndex, chunkRows, chunk);
				break;
			} catch (error) {
				const status = statusOf(error);
				if (control.aborted) return result('aborted', chunkIndex);

				if (status === 401) {
					// The reauth interceptor already tried a refresh; this session is over. Wait for a
					// human to sign in and press Resume rather than burning retries against it.
					control.paused = true;
					progress(chunkIndex, 'signed-out');
					await waitWhilePaused(control, sleep);
					if (control.aborted) return result('aborted', chunkIndex);
					continue;
				}
				if (status === 429 && rateLimitWaits < 10) {
					rateLimitWaits += 1;
					progress(chunkIndex, 'rate-limited');
					await sleep(retryAfterMs(error));
					continue;
				}
				if (isTransient(error) && attempt < maxRetries) {
					attempt += 1;
					progress(chunkIndex, 'retrying');
					await sleep(1000 * 2 ** (attempt - 1));
					continue;
				}
				return result('failed', chunkIndex, error);
			}
		}

		progress(chunkIndex + 1);
	}

	if (control.aborted) return result('aborted', chunkCount);

	try {
		await transport.complete(batch.id);
	} catch (error) {
		// Every row is in; only the bookkeeping call failed. Report it, but the counts stand.
		return result('failed', chunkCount, error);
	}

	return result('completed', chunkCount);
};
